import { ref, shallowRef, type Ref } from 'vue';
import { useTravelataAppConfig } from '@/shared/composables';
import type { PromoPlacement } from '@/shared/di';
import { destroyAdfox, whenAdfoxReady, type AdfoxImageCreative } from './adfox';
import { A51BannerState } from './types';
import { A51_LOADING_TIMEOUT_MS } from '../config/a51';
import { getAdfoxImageCreative } from '../lib/adfoxCreative';

interface A51Creative {
  placement: PromoPlacement;
  container: HTMLDivElement;
  host: HTMLElement | null;
  dispose: (() => void) | null;
}

interface UseA51Creative {
  state: Readonly<Ref<A51BannerState>>;
  creative: Readonly<Ref<AdfoxImageCreative | null>>;
}

const state = ref<A51BannerState>(A51BannerState.Loading);
const creative = shallowRef<AdfoxImageCreative | null>(null);

let currentCreative: A51Creative | null = null;

function loadAdfoxCreative(placement: PromoPlacement, containerId: string): (() => void) | null {
  const { promoPlacementsOwnerId } = useTravelataAppConfig();

  state.value = A51BannerState.Loading;
  creative.value = null;

  if (!promoPlacementsOwnerId || !placement.customParam1 || !placement.customParam2) {
    state.value = A51BannerState.Stub;

    return null;
  }

  let disposed = false;
  let created = false;

  const showStub = (): void => {
    if (!disposed) {
      state.value = A51BannerState.Stub;
    }
  };

  const loadingTimeout = setTimeout(() => {
    if (state.value === A51BannerState.Loading) {
      showStub();
    }
  }, A51_LOADING_TIMEOUT_MS);

  whenAdfoxReady((adfoxCode) => {
    if (disposed) {
      return;
    }

    adfoxCode.create({
      ownerId: promoPlacementsOwnerId,
      containerId,
      params: {
        p1: placement.customParam1,
        p2: placement.customParam2,
        puid: Date.now(),
        pcountry: Number(placement.countryId),
      },
      onRender: (data) => {
        if (!disposed) {
          creative.value = getAdfoxImageCreative(data);
          state.value = A51BannerState.Loaded;
        }
      },
      onStub: showStub,
      onError: showStub,
    });
    created = true;
  });

  return () => {
    disposed = true;
    clearTimeout(loadingTimeout);

    if (created) {
      destroyAdfox(containerId);
    }
  };
}

export function disposeA51Creative(): void {
  if (!currentCreative) {
    return;
  }

  currentCreative.dispose?.();
  currentCreative.container.remove();
  currentCreative = null;
  state.value = A51BannerState.Loading;
  creative.value = null;
}

function startA51Creative(placement: PromoPlacement, host: HTMLElement): A51Creative {
  const container = document.createElement('div');
  container.id = placement.containerId;
  host.append(container);

  return { placement, container, host, dispose: loadAdfoxCreative(placement, placement.containerId) };
}

export function attachA51Creative(placement: PromoPlacement, host: HTMLElement): void {
  if (currentCreative?.placement === placement) {
    currentCreative.host = host;
    host.append(currentCreative.container);

    return;
  }

  disposeA51Creative();
  currentCreative = startA51Creative(placement, host);
}

export function releaseA51Creative(host: HTMLElement): void {
  if (!currentCreative || currentCreative.host !== host) {
    return;
  }

  const releasedCreative = currentCreative;
  releasedCreative.host = null;

  setTimeout(() => {
    if (currentCreative === releasedCreative && !releasedCreative.host) {
      disposeA51Creative();
    }
  });
}

export function useA51Creative(): UseA51Creative {
  return { state, creative };
}
